import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  leadJobStatusLabel,
  leadService,
  type LeadJobStatus,
  type LeadSearchJob,
} from '../services/leadService';
import { CITIES_BY_PROVINCE, PROVINCES } from '../data/iran-geo';
import { fieldClass, labelClass, formatDateTime } from '../utils/form';

const emptyForm = () => ({
  city: 'تهران',
  job: 'گل مصنوعی',
  requestedLimit: 100,
});

function faNum(n: number): string {
  return String(n).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)] ?? d);
}

function statusBadge(status: LeadJobStatus) {
  const styles: Record<LeadJobStatus, string> = {
    pending: 'bg-slate-100 text-slate-700',
    running: 'bg-amber-100 text-amber-800',
    completed: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-rose-100 text-rose-800',
    cancelled: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${styles[status]}`}>
      {leadJobStatusLabel[status]}
    </span>
  );
}

function searchPreview(job: string, city: string): string {
  const j = job.trim();
  const c = city.trim();
  if (!j || !c) return '';
  return `${j} در ${c}`;
}

export function BusinessSearchPage() {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState<LeadSearchJob[]>([]);
  const [loading, setLoading] = useState(true);

  const allCities = useMemo(() => {
    const set = new Set<string>();
    for (const province of PROVINCES) {
      for (const city of CITIES_BY_PROVINCE[province]) {
        set.add(city);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fa'));
  }, []);

  const preview = searchPreview(form.job, form.city);

  const hasActive = useMemo(
    () => jobs.some((j) => j.status === 'pending' || j.status === 'running'),
    [jobs],
  );

  const load = useCallback(async () => {
    try {
      const res = await leadService.listJobs({ page: 1, limit: 30 });
      setJobs(res.data ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت جاب‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!hasActive) return;
    const t = window.setInterval(() => {
      void load();
    }, 2500);
    return () => window.clearInterval(t);
  }, [hasActive, load]);

  const startSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const city = form.city.trim();
    const job = form.job.trim();
    const limit = Number(form.requestedLimit);
    if (!city) {
      toast.error('شهر را از لیست انتخاب کنید');
      return;
    }
    if (!job) {
      toast.error('شغل / عبارت جستجو الزامی است');
      return;
    }
    if (!Number.isFinite(limit) || limit < 1) {
      toast.error('محدودیت تعداد نتایج را مشخص کنید');
      return;
    }
    setSubmitting(true);
    try {
      await leadService.createJob({
        city,
        category: job,
        query: job,
        requestedLimit: Math.min(5000, Math.max(1, limit)),
        requirePhone: true,
        requireWebsite: false,
      });
      toast.success(`جاب «${preview}» ایجاد شد`);
      setLoading(true);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'ایجاد جاب ناموفق بود');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelJob = async (id: string) => {
    try {
      await leadService.cancelJob(id);
      toast.success('درخواست لغو ثبت شد');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'لغو ناموفق بود');
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">جستجوی کسب‌وکار</h1>
          <p className="mt-1 text-sm text-slate-500">
            مثل کتاب اول: «شغل در شهر» — فقط شماره موبایل استخراج می‌شود.
          </p>
        </div>
        <Link
          to="/leads"
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          مدیریت لیدها
        </Link>
      </div>

      <form
        onSubmit={(e) => void startSearch(e)}
        className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2"
      >
        <div>
          <label className={labelClass}>شهر</label>
          <select
            className={fieldClass}
            value={form.city}
            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            required
          >
            <option value="">انتخاب شهر…</option>
            {allCities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>شغل / عبارت جستجو</label>
          <input
            className={fieldClass}
            value={form.job}
            onChange={(e) => setForm((f) => ({ ...f, job: e.target.value }))}
            placeholder="مثلاً تالار، گل فروشی، گل مصنوعی"
            required
          />
        </div>
        <div>
          <label className={labelClass}>محدودیت تعداد نتایج</label>
          <input
            type="number"
            min={1}
            max={5000}
            className={fieldClass}
            value={form.requestedLimit}
            onChange={(e) => setForm((f) => ({ ...f, requestedLimit: Number(e.target.value) }))}
            dir="ltr"
            required
          />
        </div>
        <div className="flex items-end">
          <div className="w-full rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            لینک جستجو:{' '}
            <strong className="text-slate-900">{preview || '—'}</strong>
            {preview ? (
              <span className="mt-1 block text-xs text-slate-500" dir="ltr">
                avval.ir/search/{preview.replace(/\s+/g, '-')}
              </span>
            ) : null}
          </div>
        </div>
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            {submitting ? 'در حال ثبت…' : 'شروع جستجو'}
          </button>
        </div>
      </form>

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-3">
          <h2 className="text-sm font-bold text-slate-800">جاب‌های اخیر</h2>
        </div>
        {loading ? (
          <p className="p-5 text-sm text-slate-500">در حال بارگذاری…</p>
        ) : jobs.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">هنوز جابی ثبت نشده است.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {jobs.map((job) => {
              const pagesProgress =
                job.pagesCrawled > 0 || job.status === 'running'
                  ? Math.min(100, Math.round((job.pagesCrawled / Math.max(job.requestedLimit, job.pagesCrawled || 1)) * 100))
                  : job.requestedLimit > 0
                    ? Math.min(100, Math.round((job.totalFound / job.requestedLimit) * 100))
                    : 0;
              return (
                <div key={job.id} className="space-y-3 px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-slate-900">
                        {job.category || job.query} در {job.city}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        ایجاد: {formatDateTime(job.createdAt)} · حد: {faNum(job.requestedLimit)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {statusBadge(job.status)}
                      {(job.status === 'pending' || job.status === 'running') && (
                        <button
                          type="button"
                          onClick={() => void cancelJob(job.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-bold text-rose-600 hover:bg-rose-50"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          لغو
                        </button>
                      )}
                    </div>
                  </div>
                  {(job.status === 'running' || job.status === 'pending') && (
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-indigo-500 transition-all"
                        style={{ width: `${pagesProgress}%` }}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 sm:grid-cols-3 lg:grid-cols-6">
                    <div>
                      صفحات:{' '}
                      <strong>
                        {faNum(job.pagesCrawled)} / {faNum(Math.max(job.requestedLimit, job.pagesCrawled))}
                      </strong>
                    </div>
                    <div>
                      یافت‌شده: <strong>{faNum(job.totalFound)}</strong>
                    </div>
                    <div>
                      ذخیره‌شده: <strong>{faNum(job.totalSaved)}</strong>
                    </div>
                    <div>
                      تکراری: <strong>{faNum(job.totalDuplicates)}</strong>
                    </div>
                    <div>
                      ردشده: <strong>{faNum(job.pagesSkipped)}</strong>
                    </div>
                    <div>
                      خطا: <strong>{faNum(job.crawlErrors)}</strong>
                    </div>
                  </div>
                  {(job.currentUrl || job.currentDomain) && (job.status === 'running' || job.status === 'pending') ? (
                    <p className="truncate text-xs text-slate-500" dir="ltr" title={job.currentUrl}>
                      {job.currentDomain ? `${job.currentDomain} · ` : ''}
                      {job.currentUrl}
                    </p>
                  ) : null}
                  {job.errorMessage ? (
                    <p className="text-xs font-semibold text-rose-600">{job.errorMessage}</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
